import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll';
import { Seo, SITE_URL } from "./Seo";
import JsonData from "../data/data.json";

const projects = JsonData?.Gallery || [];

const ScreenshotGallery = ({ screenshots, title }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const prev = () => setActiveIndex((i) => (i - 1 + screenshots.length) % screenshots.length);
  const next = () => setActiveIndex((i) => (i + 1) % screenshots.length);

  return (
    <div className="cs-screenshot-gallery">
      <div className="cs-screenshot-main">
        <button className="cs-ss-arrow cs-ss-prev" onClick={prev} aria-label="Previous">
          <i className="fa fa-chevron-left"></i>
        </button>
        <img
          src={`../${screenshots[activeIndex]}`}
          alt={`${title} screenshot ${activeIndex + 1}`}
          className="cs-screenshot-img"
        />
        <button className="cs-ss-arrow cs-ss-next" onClick={next} aria-label="Next">
          <i className="fa fa-chevron-right"></i>
        </button>
        <div className="cs-ss-counter">{activeIndex + 1} / {screenshots.length}</div>
      </div>
      <div className="cs-screenshot-thumbs">
        {screenshots.map((src, i) => (
          <button
            key={i}
            className={`cs-ss-thumb${i === activeIndex ? ' active' : ''}`}
            onClick={() => setActiveIndex(i)}
            aria-label={`Screenshot ${i + 1}`}
          >
            <img src={`../${src}`} alt={`${title} ${i + 1}`} />
          </button>
        ))}
      </div>
    </div>
  );
};

export const CaseStudiesDetail = () => {
  const { slug } = useParams();
  const currentIndex = projects.findIndex((item) => item.slug === slug);
  const project = projects[currentIndex];

  const prev = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const next = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  const handleNav = () => {
    scroll.scrollToTop({ duration: 0 });
  };

  if (!project) {
    return (
      <div id="caseStudies-detail">
        <Seo
          path={`/case-studies/${slug}`}
          title="Project Not Found | Pandobit"
          description="The case study you are looking for could not be found."
          noindex
        />
        <div className="container text-center" style={{ padding: '80px 0' }}>
          <h2>Project Not Found</h2>
          <Link to="/#case-studies" onClick={handleNav} className="btn btn-custom btn-lg" style={{ marginTop: 24 }}>
            Back to Case Studies
          </Link>
        </div>
      </div>
    );
  }

  const caseStudySchema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    headline: `${project.title} — Case Study`,
    description: project.overview || project.text,
    image: `${SITE_URL}/${project.largeImage}`,
    url: `${SITE_URL}/case-studies/${slug}`,
    about: project.industry,
    creator: { "@type": "Organization", name: "Pandobit", url: SITE_URL },
  };

  return (
    <div id="caseStudies-detail">

      <Seo
        path={`/case-studies/${slug}`}
        title={`${project.title} — Case Study | Pandobit`}
        description={project.overview || project.text}
        image={`${SITE_URL}/${project.largeImage}`}
        type="article"
        jsonLd={caseStudySchema}
      />

      <div className="cs-hero">
        <img src={`../${project.largeImage}`} alt={project.title} />
        <div className="cs-hero-overlay">
          <div className="container">
            <div className="cs-hero-meta">
              {project.industry && <span className="cs-tag">{project.industry}</span>}
              {project.platform && <span className="cs-tag">{project.platform}</span>}
            </div>
            <h1>{project.title}</h1>
            <p className="cs-hero-sub">{project.text}</p>
          </div>
        </div>
      </div>

      <div className="container cs-body">

        {project.overview && (
          <section className="cs-section">
            <h2>Project Overview</h2>
            <p className="cs-overview">{project.overview}</p>
          </section>
        )}

        <div className="cs-two-col">
          {project.role && (
            <section className="cs-section">
              <h2>Our Contribution</h2>
              <ul className="cs-list">
                {project.role.map((item, i) => (
                  <li key={i}>
                    <i className="fa fa-check-circle"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {project.techStack && (
            <section className="cs-section">
              <h2>Tech Stack</h2>
              <div className="cs-tech-stack">
                {project.techStack.map((tech, i) => (
                  <span className="cs-tech-tag" key={i}>{tech}</span>
                ))}
              </div>
            </section>
          )}
        </div>

        {project.highlights && (
          <section className="cs-section">
            <h2>Key Features & Highlights</h2>
            <div className="cs-highlights">
              {project.highlights.map((item, i) => (
                <div className="cs-highlight-card" key={i}>
                  <i className="fa fa-star"></i>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {project.screenshots && project.screenshots.length > 0 && (
          <section className="cs-section cs-screenshots-section">
            <h2>App Screenshots</h2>
            <ScreenshotGallery screenshots={project.screenshots} title={project.title} />
          </section>
        )}

        {project.storeUrl && (
          <div className="cs-footer-row">
            <a href={project.storeUrl} target="_blank" rel="noreferrer" className="btn btn-custom btn-lg cs-store-btn">
              <i className="fa fa-external-link" style={{ marginRight: 8 }}></i>
              {project.storeLabel || "View App"}
            </a>
          </div>
        )}

        <nav className="cs-pagination">
          <div className="cs-pagination-side">
            {prev && (
              <Link to={`/case-studies/${prev.slug}`} onClick={handleNav} className="cs-nav-btn cs-nav-prev">
                <div className="cs-nav-arrow"><i className="fa fa-arrow-left"></i></div>
                <div className="cs-nav-info">
                  <span className="cs-nav-label">Previous</span>
                  <span className="cs-nav-title">{prev.title}</span>
                  {prev.industry && <span className="cs-nav-industry">{prev.industry}</span>}
                </div>
              </Link>
            )}
          </div>

          <Link to="/#case-studies" onClick={handleNav} className="cs-nav-all">
            <i className="fa fa-th-large"></i>
            <span>All Projects</span>
          </Link>

          <div className="cs-pagination-side cs-pagination-right">
            {next && (
              <Link to={`/case-studies/${next.slug}`} onClick={handleNav} className="cs-nav-btn cs-nav-next">
                <div className="cs-nav-info">
                  <span className="cs-nav-label">Next</span>
                  <span className="cs-nav-title">{next.title}</span>
                  {next.industry && <span className="cs-nav-industry">{next.industry}</span>}
                </div>
                <div className="cs-nav-arrow"><i className="fa fa-arrow-right"></i></div>
              </Link>
            )}
          </div>
        </nav>

        <section className="service-detail-bridge">
          <p className="service-bridge-label">Interested in working with us?</p>
          <h2>
            We'd love to help you<br />
            build your next product.
          </h2>
          <p className="service-bridge-sub">
            Whether you have a clear brief or just an early idea — reach out below and our team will get back to you within one business day.
          </p>
          <div className="service-bridge-arrow">
            <i className="fa fa-angle-down"></i>
          </div>
        </section>

      </div>
    </div>
  );
};
